import type { COMMON_ORPC_ERROR_DEFS } from '@orpc/client';
import { ORPCError, type ClientContext } from '@orpc/client';
import { OpenAPILink } from '@orpc/openapi-client/fetch';
import {
  isContractProcedure,
  type AnyContractRouter,
  type AnySchema,
  type ContractProcedure,
  type ErrorMap,
  type InferSchemaInput,
  type InferSchemaOutput,
  type Meta,
} from '@orpc/contract';
import type { StandardHeaders } from '@orpc/standard-server';

import { getTerraAuthHeader } from './auth-header-provider';
import { getTerraRequestHeaders } from './request-headers-provider';
import { resolveGatewayServiceBaseUrl } from './gateway-service-base-url';

type Credentials = 'include' | 'omit' | 'same-origin';

export type TerraOrpcSuccessStatus = 200 | 201 | 202 | 204 | 206;

export type TerraOrpcErrorStatus =
  | 300
  | 301
  | 302
  | 303
  | 304
  | 307
  | 308
  | 400
  | 401
  | 402
  | 403
  | 404
  | 405
  | 406
  | 407
  | 408
  | 409
  | 410
  | 411
  | 412
  | 413
  | 414
  | 415
  | 416
  | 417
  | 418
  | 422
  | 423
  | 424
  | 425
  | 426
  | 428
  | 429
  | 431
  | 451
  | 500
  | 501
  | 502
  | 503
  | 504
  | 505
  | 506
  | 507
  | 508
  | 510
  | 511;

type TerraOrpcSuccessResponse<TBody> =
  TerraOrpcSuccessStatus extends infer TStatus
    ? TStatus extends number
      ? { status: TStatus; body: TBody; headers: Headers }
      : never
    : never;

/**
 * Contract error map items widen `status` literals to `number`, so the wire
 * status is derived from the error code's canonical status instead. All Terra
 * contracts declare canonical code/status pairs (enforced by convention).
 */
type TerraOrpcErrorCodeStatus<TCode> =
  TCode extends keyof typeof COMMON_ORPC_ERROR_DEFS
    ? (typeof COMMON_ORPC_ERROR_DEFS)[TCode]['status']
    : never;

/**
 * Statuses whose wire body is typed by the contract's `.errors()` map
 * (declared with a `data` schema).
 */
type TerraOrpcDeclaredErrorStatus<TErrorMap extends ErrorMap> = {
  [K in keyof TErrorMap]: NonNullable<TErrorMap[K]> extends {
    data: AnySchema;
  }
    ? TerraOrpcErrorCodeStatus<K> extends infer TStatus extends
        TerraOrpcErrorStatus
      ? TStatus
      : never
    : never;
}[keyof TErrorMap];

type TerraOrpcDeclaredErrorResponse<TErrorMap extends ErrorMap> = {
  [K in keyof TErrorMap]: NonNullable<TErrorMap[K]> extends {
    data: infer TDataSchema extends AnySchema;
  }
    ? TerraOrpcErrorCodeStatus<K> extends infer TStatus extends
        TerraOrpcErrorStatus
      ? {
          status: TStatus;
          body: InferSchemaOutput<TDataSchema>;
          headers: Headers;
        }
      : never
    : never;
}[keyof TErrorMap];

type TerraOrpcUndeclaredErrorResponse<TErrorMap extends ErrorMap> =
  Exclude<
    TerraOrpcErrorStatus,
    TerraOrpcDeclaredErrorStatus<TErrorMap>
  > extends infer TStatus
    ? TStatus extends number
      ? { status: TStatus; body: unknown; headers: Headers }
      : never
    : never;

export type TerraOrpcResponse<
  TBody,
  TErrorMap extends ErrorMap = Record<never, never>,
> =
  | TerraOrpcSuccessResponse<TBody>
  | TerraOrpcDeclaredErrorResponse<TErrorMap>
  | TerraOrpcUndeclaredErrorResponse<TErrorMap>;

export type TerraOrpcRequestExtras = {
  extraHeaders?: Record<string, string | undefined>;
  fetchOptions?: RequestInit;
  overrideClientOptions?: { baseUrl?: string; credentials?: Credentials };
};

type CompatOptionalKeys<T> = {
  [K in keyof T]: Record<string, never> extends T[K] ? K : never;
}[keyof T];

/**
 * Mirror Terra API client ergonomics: an input key (params/query/body/headers)
 * is optional at the call site when an empty object satisfies its type (e.g. a
 * query whose fields are all optional, or an empty body), otherwise required.
 */
type CompatInput<T> = Partial<Pick<T, CompatOptionalKeys<T>>> &
  Omit<T, CompatOptionalKeys<T>>;

type TerraOrpcCallArgs<TInputSchema extends AnySchema> =
  InferSchemaInput<TInputSchema> extends object
    ? CompatInput<InferSchemaInput<TInputSchema>> & TerraOrpcRequestExtras
    : TerraOrpcRequestExtras;

type TerraOrpcRouteFunction<
  TInputSchema extends AnySchema,
  TOutputSchema extends AnySchema,
  TErrorMap extends ErrorMap,
> =
  Record<string, never> extends TerraOrpcCallArgs<TInputSchema>
    ? (
        args?: TerraOrpcCallArgs<TInputSchema>,
      ) => Promise<
        TerraOrpcResponse<InferSchemaOutput<TOutputSchema>, TErrorMap>
      >
    : (
        args: TerraOrpcCallArgs<TInputSchema>,
      ) => Promise<
        TerraOrpcResponse<InferSchemaOutput<TOutputSchema>, TErrorMap>
      >;

export type TerraOrpcClient<TContract extends AnyContractRouter> = {
  [TKey in keyof TContract]: TContract[TKey] extends ContractProcedure<
    infer UInputSchema,
    infer UOutputSchema,
    infer UErrorMap,
    Meta
  >
    ? TerraOrpcRouteFunction<UInputSchema, UOutputSchema, UErrorMap>
    : TContract[TKey] extends AnyContractRouter
      ? TerraOrpcClient<TContract[TKey]>
      : never;
};

type ResponseHolder = {
  status?: number;
  headers?: StandardHeaders;
};

type TerraOrpcRuntimeArgs = TerraOrpcRequestExtras & {
  params?: Record<string, unknown>;
  query?: unknown;
  body?: unknown;
  headers?: Record<string, string | undefined>;
};

type TerraOrpcCallContext = ClientContext & {
  responseHolder?: ResponseHolder;
  callerHeaders?: StandardHeaders;
  overrideBaseUrl?: string;
  overrideCredentials?: Credentials;
  fetchOptions?: RequestInit;
};

class TerraOrpcResponseError extends ORPCError<string, unknown> {
  readonly responseBody: unknown;
  readonly responseHeaders: StandardHeaders;

  constructor({
    status,
    body,
    headers,
  }: {
    status: number;
    body: unknown;
    headers: StandardHeaders;
  }) {
    super('TERRA_HTTP_RESPONSE', { status, data: body });
    this.responseBody = body;
    this.responseHeaders = headers;
  }
}

const DEFAULT_TERRA_ORPC_CLIENT_CREDENTIALS: Credentials = 'include';

export type CreateTerraOrpcClientArgs<TContract extends AnyContractRouter> = {
  contract: TContract;
  serviceName?: string;
  credentials?: Credentials;
  resolveBaseUrl?: () => string;
};

function isAbsoluteHttpUrl(path: string): boolean {
  return path.startsWith('http://') || path.startsWith('https://');
}

function toAbsoluteBaseUrl(baseUrl: string): string {
  if (isAbsoluteHttpUrl(baseUrl)) {
    return baseUrl;
  }
  const origin = globalThis.location?.origin;
  if (origin) {
    return new URL(baseUrl || '/', origin).toString();
  }
  return baseUrl;
}

function normalizeHeaderRecord(
  headers: Record<string, string | undefined> | undefined,
): StandardHeaders {
  return Object.fromEntries(
    Object.entries(headers ?? {})
      .filter(([, headerValue]) => headerValue !== undefined)
      .map(([headerName, headerValue]) => [
        headerName.toLowerCase(),
        headerValue,
      ]),
  );
}

function toWebHeaders(headers: StandardHeaders | undefined): Headers {
  const webHeaders = new Headers();
  for (const [headerName, headerValue] of Object.entries(headers ?? {})) {
    if (headerValue === undefined) continue;
    for (const singleValue of Array.isArray(headerValue)
      ? headerValue
      : [headerValue]) {
      webHeaders.append(headerName, singleValue);
    }
  }
  return webHeaders;
}

export function createTerraOrpcClient<TContract extends AnyContractRouter>(
  params: CreateTerraOrpcClientArgs<TContract>,
): TerraOrpcClient<TContract> {
  const { contract } = params;
  const credentials =
    params.credentials ?? DEFAULT_TERRA_ORPC_CLIENT_CREDENTIALS;

  const resolveBaseUrl = (context: TerraOrpcCallContext): string => {
    if (context.overrideBaseUrl) {
      return toAbsoluteBaseUrl(context.overrideBaseUrl);
    }
    if (typeof params.resolveBaseUrl === 'function') {
      return toAbsoluteBaseUrl(params.resolveBaseUrl());
    }
    return toAbsoluteBaseUrl(
      resolveGatewayServiceBaseUrl({ serviceName: params.serviceName }),
    );
  };

  const link = new OpenAPILink<TerraOrpcCallContext>(contract, {
    url: (options) => resolveBaseUrl(options.context),
    headers: (options) => {
      const authHeader = getTerraAuthHeader();
      return {
        ...options.context.callerHeaders,
        ...normalizeHeaderRecord(getTerraRequestHeaders()),
        ...(authHeader ? { authorization: authHeader } : {}),
      };
    },
    fetch: (request, init, options) => {
      const requestWithCredentials = new Request(request, {
        ...init,
        redirect: 'follow',
        credentials: options.context.overrideCredentials ?? credentials,
        ...options.context.fetchOptions,
      });

      return globalThis.fetch(requestWithCredentials);
    },
    clientInterceptors: [
      async (options) => {
        const response = await options.next();
        const holder = options.context.responseHolder;
        if (holder) {
          holder.status = response.status;
          holder.headers = response.headers;
        }
        return response;
      },
    ],
    customErrorResponseBodyDecoder: (deserializedBody, response) =>
      new TerraOrpcResponseError({
        status: response.status,
        body: deserializedBody,
        headers: response.headers,
      }),
  });

  const callProcedure = async (
    path: readonly string[],
    args: TerraOrpcRuntimeArgs = {},
  ) => {
    const {
      extraHeaders,
      fetchOptions,
      overrideClientOptions,
      params: pathParams,
      query,
      body,
      headers,
    } = args;
    const responseHolder: ResponseHolder = {};
    const context: TerraOrpcCallContext = {
      responseHolder,
      callerHeaders: {
        ...normalizeHeaderRecord(headers),
        ...normalizeHeaderRecord(extraHeaders),
      },
      overrideBaseUrl: overrideClientOptions?.baseUrl,
      overrideCredentials: overrideClientOptions?.credentials,
      fetchOptions,
    };

    try {
      const output = await link.call(
        path,
        { params: pathParams, query, body },
        { context, signal: fetchOptions?.signal ?? undefined },
      );
      return {
        status: responseHolder.status ?? 200,
        body: output,
        headers: toWebHeaders(responseHolder.headers),
      };
    } catch (error) {
      if (error instanceof TerraOrpcResponseError) {
        return {
          status: error.status,
          body: error.responseBody,
          headers: toWebHeaders(error.responseHeaders),
        };
      }
      throw error;
    }
  };

  const buildNode = (
    node: AnyContractRouter,
    path: readonly string[],
  ): unknown => {
    if (isContractProcedure(node)) {
      return (args?: TerraOrpcRuntimeArgs) => callProcedure(path, args);
    }
    return Object.fromEntries(
      Object.entries(node).map(([key, child]) => [
        key,
        buildNode(child, [...path, key]),
      ]),
    );
  };

  return buildNode(contract, []) as TerraOrpcClient<TContract>;
}
